import { motion } from "framer-motion";
import { Wrench, TrendingUp, Layers, Zap, BrainCircuit, Sparkles, ArrowRight } from "lucide-react";
import { scoreTone } from "@/lib/api";

const PHASE_ICONS = [Wrench, TrendingUp, Layers, Zap, BrainCircuit];

export default function TransformationPlan({ plan, onCTA }) {
  if (!plan) return null;
  return (
    <div id="transformation-plan" className="mt-8" data-testid="transformation-plan">
      <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 md:p-10">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} className="text-brand" />
          <span className="overline">Your Website Transformation Plan · AI generated</span>
        </div>
        <h3 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
          From audit to intelligent platform
        </h3>
        <p className="text-slate-600 leading-relaxed max-w-3xl">{plan.executive_summary}</p>

        <div className="flex flex-wrap items-center gap-4 mt-6">
          {plan.projected_score != null && (
            <div className="inline-flex items-center gap-3 border border-slate-200 rounded-xl bg-slate-50 px-5 py-3">
              <span className="overline">Projected Score</span>
              <span className="font-heading font-extrabold text-3xl" style={{ color: scoreTone(plan.projected_score).color }}>
                {plan.projected_score}
              </span>
            </div>
          )}
        </div>

        {plan.quick_wins?.length > 0 && (
          <div className="mt-6">
            <p className="overline mb-2">Quick Wins</p>
            <div className="flex flex-wrap gap-2">
              {plan.quick_wins.map((q, i) => (
                <span key={i} className="text-xs font-medium border border-blue-200 bg-blue-50 text-brand px-3 py-1.5 rounded-full">{q}</span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          {(plan.phases || []).map((phase, i) => {
            const Icon = PHASE_ICONS[i % PHASE_ICONS.length];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="border border-slate-200 rounded-xl bg-slate-50/60 p-6 hover-lift hover:border-brand/40"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-brand">
                    <Icon size={18} />
                  </div>
                  <span className="font-heading font-bold text-lg text-slate-900">{phase.name}</span>
                </div>
                <p className="text-slate-500 text-sm mb-4">{phase.goal}</p>
                <ul className="space-y-2">
                  {(phase.items || []).map((it, j) => (
                    <li key={j} className="flex gap-2.5 text-sm text-slate-600">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 border-t border-slate-200 pt-8">
          <p className="font-heading text-xl md:text-2xl font-bold text-slate-900 flex-1">Let WebsiteTech build this for you.</p>
          <button onClick={onCTA} data-testid="plan-cta" className="btn-primary rounded-xl px-7 py-3.5 flex items-center gap-2">
            Book a Strategy Session <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
